import React from "react";

const ImageMaskEditor = React.memo(function ImageMaskEditor({ image, onChangeBase64 }) {
  const isMobile = window.innerWidth < 768;

  const modalStyles = {
    darkBtn: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "#121821",
      color: "#E5E7EB",
      border: "1px solid #2B3440",
      borderRadius: 10,
      padding: isMobile ? "8px 10px" : "10px 16px",
      fontSize: isMobile ? 13 : 14,
      fontWeight: 600,
      cursor: "pointer",
      flex: isMobile ? "1 1 45%" : "initial",
    },
  };

  const canvasRef = React.useRef(null);
  const historyRef = React.useRef([]);
  const stepRef = React.useRef(0);
  const initialImageRef = React.useRef(null);

  const [drawing, setDrawing] = React.useState(false);
  const [brush, setBrush] = React.useState(18);
  const [color, setColor] = React.useState("#ff0000");
  const [mode, setMode] = React.useState("draw");

  // 🔒 Load image ONLY ONCE
  React.useEffect(() => {
    if (initialImageRef.current) return;
    initialImageRef.current = image;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = image;

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      historyRef.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
      stepRef.current = 0;
    };
  }, [image]);

  // ✅ unified coordinate mapper (mouse/touch/pen)
  const getPoint = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // pointer events always have clientX/clientY
    const cx = evt.clientX;
    const cy = evt.clientY;

    return {
      x: (cx - rect.left) * (canvas.width / rect.width),
      y: (cy - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    setDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPoint(e);

    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const draw = (e) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPoint(e);

    ctx.lineWidth = brush;
    ctx.lineCap = "round";
    ctx.globalCompositeOperation = mode === "erase" ? "destination-out" : "source-over";
    ctx.strokeStyle = color;

    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    historyRef.current = historyRef.current.slice(0, stepRef.current + 1);
    historyRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    stepRef.current++;

    onChangeBase64(canvas.toDataURL("image/png"));
  };

  const undo = () => {
    if (stepRef.current <= 0) return;
    stepRef.current--;
    const ctx = canvasRef.current.getContext("2d");
    ctx.putImageData(historyRef.current[stepRef.current], 0, 0);
  };

  const downloadEditedImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "edited_image.png";
    link.click();
  };

  // ✅ Pointer handlers (work on desktop + mobile)
  const onPointerDown = (e) => {
    // stop page scroll while drawing
    e.preventDefault();
    // capture pointer so move continues even if finger goes outside canvas
    e.currentTarget.setPointerCapture?.(e.pointerId);
    start(e);
  };

  const onPointerMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    draw(e);
  };

  const onPointerUp = (e) => {
    e.preventDefault();
    end();
  };

  return (
    <div style={{ width: "100%" }}>
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: 450,
          borderRadius: 12,
          border: "1px dashed #333",
          background: "#000",
          touchAction: "none", // ✅ IMPORTANT for mobile drawing
        }}
        // ✅ replace mouse events with pointer events
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
      />

      <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <input
          type="range"
          min="5"
          max="60"
          value={brush}
          onChange={(e) => setBrush(+e.target.value)}
        />

        {["#ff0000", "#00ffcc", "#ffaa00", "#ffffff"].map((c) => (
          <div
            key={c}
            onClick={() => {
              setColor(c);
              setMode("draw");
            }}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: c,
              cursor: "pointer",
            }}
          />
        ))}

        <button onClick={undo} style={modalStyles.darkBtn}>
          Undo
        </button>
        <button onClick={downloadEditedImage} style={modalStyles.darkBtn}>
          Download
        </button>
      </div>
    </div>
  );
});

export default ImageMaskEditor;